import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
    Calendar as CalendarIcon,
    Clock,
    Plus,
    Trash2,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Users,
    CheckCircle,
    AlertCircle,
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time slot options
const timeSlots = [];
for (let h = 8; h <= 19; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
}

export const PlanningManager = () => {
    const { user, token } = useAuth();
    const [availabilities, setAvailabilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [addingSlot, setAddingSlot] = useState(false);

    // Single slot form
    const [newSlotStart, setNewSlotStart] = useState('09:00');
    const [newSlotEnd, setNewSlotEnd] = useState('10:00');

    // Bulk add form
    const [bulkDates, setBulkDates] = useState([]);
    const [bulkStartTime, setBulkStartTime] = useState('09:00');
    const [bulkEndTime, setBulkEndTime] = useState('18:00');
    const [bulkInterval, setBulkInterval] = useState('60'); // minutes

    useEffect(() => {
        fetchAvailabilities();
    }, []);

    const fetchAvailabilities = async () => {
        try {
            const response = await axios.get(`${API}/availabilities/my`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailabilities(response.data);
        } catch (error) {
            console.error('Error fetching availabilities:', error);
        } finally {
            setLoading(false);
        }
    };

    const addSingleSlot = async () => {
        if (!selectedDate) {
            toast.error('Sélectionnez une date');
            return;
        }

        setAddingSlot(true);
        try {
            await axios.post(
                `${API}/availabilities`,
                {
                    date: format(selectedDate, 'yyyy-MM-dd'),
                    start_time: newSlotStart,
                    end_time: newSlotEnd,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Créneau ajouté');
            setShowAddModal(false);
            fetchAvailabilities();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setAddingSlot(false);
        }
    };

    const addBulkSlots = async () => {
        if (bulkDates.length === 0) {
            toast.error('Sélectionnez au moins une date');
            return;
        }

        setAddingSlot(true);
        const interval = parseInt(bulkInterval);
        let successCount = 0;
        let errorCount = 0;

        for (const date of bulkDates) {
            // Generate slots for this day
            const startHour = parseInt(bulkStartTime.split(':')[0]);
            const startMin = parseInt(bulkStartTime.split(':')[1]);
            const endHour = parseInt(bulkEndTime.split(':')[0]);
            const endMin = parseInt(bulkEndTime.split(':')[1]);

            let currentMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            while (currentMinutes + interval <= endMinutes) {
                const slotStartH = Math.floor(currentMinutes / 60);
                const slotStartM = currentMinutes % 60;
                const slotEndH = Math.floor((currentMinutes + interval) / 60);
                const slotEndM = (currentMinutes + interval) % 60;

                const slotStart = `${slotStartH.toString().padStart(2, '0')}:${slotStartM.toString().padStart(2, '0')}`;
                const slotEnd = `${slotEndH.toString().padStart(2, '0')}:${slotEndM.toString().padStart(2, '0')}`;

                try {
                    await axios.post(
                        `${API}/availabilities`,
                        {
                            date: format(date, 'yyyy-MM-dd'),
                            start_time: slotStart,
                            end_time: slotEnd,
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    successCount++;
                } catch (error) {
                    errorCount++;
                }

                currentMinutes += interval;
            }
        }

        setAddingSlot(false);
        setShowBulkModal(false);
        setBulkDates([]);

        if (successCount > 0) {
            toast.success(`${successCount} créneaux ajoutés`);
        }
        if (errorCount > 0) {
            toast.error(`${errorCount} créneaux en erreur`);
        }

        fetchAvailabilities();
    };

    const deleteSlot = async (slotId) => {
        try {
            await axios.delete(`${API}/availabilities/${slotId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Créneau supprimé');
            fetchAvailabilities();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    // Get slots for a specific date
    const getSlotsForDate = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return availabilities.filter(a => a.date === dateStr);
    };

    // Calendar days
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Stats
    const totalSlots = availabilities.length;
    const bookedSlots = availabilities.filter(a => a.is_booked).length;
    const availableSlots = totalSlots - bookedSlots;

    const toggleBulkDate = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const exists = bulkDates.find(d => format(d, 'yyyy-MM-dd') === dateStr);
        
        if (exists) {
            setBulkDates(bulkDates.filter(d => format(d, 'yyyy-MM-dd') !== dateStr));
        } else {
            setBulkDates([...bulkDates, date]);
        }
    };

    if (!user || user.user_type !== 'agent') return null;

    return (
        <div className="space-y-6" data-testid="planning-manager">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading text-xl font-semibold text-scalimmo-navy flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5" />
                        Gestion du planning
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gérez vos créneaux de disponibilité
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Quick Stats */}
                    <div className="flex items-center gap-4 mr-4">
                        <div className="text-center">
                            <p className="text-lg font-semibold text-scalimmo-navy">{availableSlots}</p>
                            <p className="text-xs text-muted-foreground">Disponibles</p>
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-semibold text-green-600">{bookedSlots}</p>
                            <p className="text-xs text-muted-foreground">Réservés</p>
                        </div>
                    </div>

                    <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="gap-2">
                                <Plus className="w-4 h-4" />
                                Ajout en masse
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading">Ajouter plusieurs créneaux</DialogTitle>
                                <DialogDescription>
                                    Créez des créneaux automatiquement sur plusieurs jours
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                {/* Date Selection */}
                                <div>
                                    <Label className="mb-2 block">Sélectionnez les jours</Label>
                                    <Calendar
                                        mode="multiple"
                                        selected={bulkDates}
                                        onSelect={setBulkDates}
                                        disabled={(date) => isBefore(date, new Date())}
                                        className="rounded-md border"
                                        locale={fr}
                                    />
                                    <p className="text-xs text-muted-foreground mt-2">
                                        {bulkDates.length} jour(s) sélectionné(s)
                                    </p>
                                </div>

                                {/* Time Range */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Heure de début</Label>
                                        <Select value={bulkStartTime} onValueChange={setBulkStartTime}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Heure de fin</Label>
                                        <Select value={bulkEndTime} onValueChange={setBulkEndTime}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Interval */}
                                <div>
                                    <Label>Durée de chaque créneau</Label>
                                    <Select value={bulkInterval} onValueChange={setBulkInterval}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="30">30 minutes</SelectItem>
                                            <SelectItem value="45">45 minutes</SelectItem>
                                            <SelectItem value="60">1 heure</SelectItem>
                                            <SelectItem value="90">1h30</SelectItem>
                                            <SelectItem value="120">2 heures</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    className="w-full btn-primary"
                                    onClick={addBulkSlots}
                                    disabled={addingSlot || bulkDates.length === 0}
                                >
                                    {addingSlot ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        `Créer les créneaux (${bulkDates.length} jours)`
                                    )}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                        <DialogTrigger asChild>
                            <Button className="btn-primary gap-2" data-testid="add-slot-btn">
                                <Plus className="w-4 h-4" />
                                Ajouter un créneau
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Nouveau créneau</DialogTitle>
                                <DialogDescription>
                                    Ajoutez une disponibilité à votre calendrier
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={setSelectedDate}
                                    disabled={(date) => isBefore(date, new Date())}
                                    className="rounded-md border"
                                    locale={fr}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Début</Label>
                                        <Select value={newSlotStart} onValueChange={setNewSlotStart}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Fin</Label>
                                        <Select value={newSlotEnd} onValueChange={setNewSlotEnd}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {timeSlots.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <Button
                                    className="w-full btn-primary"
                                    onClick={addSingleSlot}
                                    disabled={addingSlot || !selectedDate}
                                >
                                    {addingSlot ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Calendar View */}
            <div className="bg-white rounded-xl border border-border/60 shadow-soft overflow-hidden">
                {/* Calendar Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy capitalize">
                        {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                    </h3>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    >
                        <ChevronRight className="w-5 h-5" />
                    </Button>
                </div>

                {/* Days Header */}
                <div className="grid grid-cols-7 border-b bg-muted/30">
                    {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                        <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                    {/* Empty cells for days before month start */}
                    {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                        <div key={`empty-${i}`} className="p-2 min-h-[100px] bg-muted/20" />
                    ))}

                    {/* Days */}
                    {days.map((day) => {
                        const slots = getSlotsForDate(day);
                        const hasSlots = slots.length > 0;
                        const bookedCount = slots.filter(s => s.is_booked).length;
                        const availableCount = slots.length - bookedCount;
                        const isPast = isBefore(day, new Date()) && !isToday(day);

                        return (
                            <div
                                key={day.toISOString()}
                                className={`p-2 min-h-[100px] border-r border-b transition-colors ${
                                    isToday(day)
                                        ? 'bg-scalimmo-gold/5'
                                        : isPast
                                        ? 'bg-muted/30'
                                        : 'hover:bg-muted/20'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${
                                        isToday(day)
                                            ? 'w-7 h-7 rounded-full bg-scalimmo-navy text-white flex items-center justify-center'
                                            : isPast
                                            ? 'text-muted-foreground'
                                            : 'text-scalimmo-navy'
                                    }`}>
                                        {format(day, 'd')}
                                    </span>
                                    {hasSlots && (
                                        <div className="flex items-center gap-1">
                                            {availableCount > 0 && (
                                                <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-blue-50 text-blue-600 border-blue-200">
                                                    {availableCount}
                                                </Badge>
                                            )}
                                            {bookedCount > 0 && (
                                                <Badge variant="outline" className="text-xs px-1.5 py-0 h-5 bg-green-50 text-green-600 border-green-200">
                                                    {bookedCount}
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Slots for this day */}
                                <div className="space-y-1">
                                    {slots.slice(0, 3).map((slot) => (
                                        <div
                                            key={slot.id}
                                            className={`text-xs px-1.5 py-1 rounded flex items-center justify-between group ${
                                                slot.is_booked
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-blue-50 text-blue-700'
                                            }`}
                                        >
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {slot.start_time}
                                            </span>
                                            {!slot.is_booked && !isPast && (
                                                <button
                                                    onClick={() => deleteSlot(slot.id)}
                                                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            )}
                                            {slot.is_booked && (
                                                <Users className="w-3 h-3" />
                                            )}
                                        </div>
                                    ))}
                                    {slots.length > 3 && (
                                        <p className="text-xs text-muted-foreground text-center">
                                            +{slots.length - 3} autres
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
                    <span className="text-muted-foreground">Disponible</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
                    <span className="text-muted-foreground">Réservé</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-scalimmo-navy text-white text-xs flex items-center justify-center">
                        {format(new Date(), 'd')}
                    </div>
                    <span className="text-muted-foreground">Aujourd'hui</span>
                </div>
            </div>

            {/* Upcoming Slots List */}
            {availabilities.filter(a => !a.is_booked).length > 0 && (
                <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy mb-4">
                        Prochains créneaux disponibles
                    </h3>
                    <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                        {availabilities
                            .filter(a => !a.is_booked && a.date >= format(new Date(), 'yyyy-MM-dd'))
                            .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time))
                            .slice(0, 10)
                            .map((slot) => (
                                <div
                                    key={slot.id}
                                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-gray-50/50 hover:bg-gray-50"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="text-center w-12">
                                            <p className="text-xs text-muted-foreground">
                                                {format(parseISO(slot.date), 'EEE', { locale: fr })}
                                            </p>
                                            <p className="text-lg font-semibold text-scalimmo-navy">
                                                {format(parseISO(slot.date), 'd')}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {format(parseISO(slot.date), 'MMM', { locale: fr })}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="font-medium text-scalimmo-navy">
                                                {slot.start_time} - {slot.end_time}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteSlot(slot.id)}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
};
